<template>
  <button
    type="button"
    :class="`
      c-button 
      c-button--${size}
      c-button--${color}
      c-button--${shape}
      ${align && `c-button--${align}`}
      ${align && `c-button--${align}`} 
      ${icon && 'c-button--icon'}
    `"
    ref="buttonRef"
    @click="handleTrackClick"
  >
    <slot />
  </button>
</template>

<script setup lang="ts">
import { inject, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

const $ga = inject('$ga') as any;
const route = useRoute();
const buttonRef = ref<HTMLElement | null>(null);
const inferredLabel = ref('');

const props = defineProps({
  size: {
    type: String,
    default: 'large',
  },
  color: {
    type: String,
    default: 'green',
  },
  shape: {
    type: String,
    default: 'sqaure',
  },
  icon: {
    type: String,
    default: '',
  },
  width: {
    type: String,
    default: '',
  },
  align: {
    type: String,
    default: 'center',
  },
  gaLabel: {
    type: String,
    default: '',
  },
  gaCategory: {
    type: String,
    default: 'feature',
  },
  gaAction: {
    type: String,
    default: 'click',
  },
  noTrack: {
    type: Boolean,
    default: false,
  },
});

onMounted(() => {
  if (buttonRef.value) {
    inferredLabel.value = (buttonRef.value.textContent || '').trim();
  }
});

const handleTrackClick = () => {
  if (props.noTrack) return;
  try {
    const eventName = `${props.gaCategory}_${props.gaAction}`;
    const label = props.gaLabel || inferredLabel.value;
    $ga?.trackEvent?.(eventName, {
      label,
      color: props.color,
      size: props.size,
      page: route.name,
    });
  } catch (e) {
    // no-op
  }
};
</script>

<style lang="scss">
.c-button {
  min-width: 12rem;
  width: width;
  padding: 0 1rem;
  border-radius: var(--radius-4);
  position: relative;
  display: flex;
  align-items: center;

  background-color: var(--white);
  &--large {
    height: 5.6rem;
    font-size: var(--font-16);
    font-weight: var(--font-medium);
  }
  &--medium {
    height: 4.8rem;
    font-size: var(--font-16);
    font-weight: var(--font-medium);
  }
  &--small {
    height: 4rem;
    font-size: var(--font-16);
    font-weight: var(--font-medium);
  }
  &--xsmall {
    min-width: 5.6rem;
    height: 3.6rem;
    font-size: var(--font-13);
    font-weight: var(--font-medium);
  }
  &--xxsmall {
    display: flex;
    min-width: 5.6rem;
    height: 3rem;
    /* padding: 0.3rem 0.5rem 0.3rem 0.3rem; */
    gap: 0rem;
    font-size: var(--font-13);
    font-weight: var(--font-medium);
    .c-icon {
      width: 2rem;
      height: 2rem;
    }
  }
  &--green {
    background-color: var(--main-30);
    color: var(--white);
    &:hover {
      background-color: var(--main-40);
    }
    &:active {
      background-color: var(--main-20);
    }
  }
  &--gray {
    background-color: var(--gray-40);
  }
  &--none {
    background-color: transparent;
    &:hover {
      background-color: var(--main-40);
    }
    &:active {
      background-color: var(--main-20);
    }
  }
  &--toggle {
    border: 1px solid var(--gray-20);
    background-color: var(--white);
    color: var(--gray-10);
  }
  &--round {
    border-radius: 100rem;
  }
  &:disabled {
    background-color: var(--gray-20);
    color: var(--white);
  }
  &--outline {
    border: 1px solid var(--gray-30);
    &:hover {
      border: 1px solid var(--main-80);
      background-color: var(--main-90);
    }
    &:active {
      border: 1px solid var(--main-80);
      background-color: var(--main-80);
    }
    &:disabled {
      background-color: var(--white);
      border: 1px solid var(--gray-30);
      color: var(--gray-20);
    }
  }
  &--icon {
    padding: 0 1.8rem 0 1.1rem;
    > i {
      margin-right: 0.6rem;
    }
  }
  &--text {
    min-width: inherit;
    padding: 0;
    background-color: transparent;
    border-color: transparent;
    color: var(--main-40);
    &:hover {
      background-color: var(--main-90);
    }
  }
  &--ai {
    height: 4rem;
    flex-shrink: 0;
    background-color: var(--white);
    border-radius: 2rem;
    border: 1.5px solid #01d596;
    color: #02be99;
    text-align: center;
    font-family: Pretendard;
    font-size: var(--font-16);
    font-style: normal;
    font-weight: var(--font-medium);
    line-height: 1.6rem;
    letter-spacing: -0.16px;
    transition: all 0.4s ease-in-out;
    moz-transition: all 0.4s ease-in-out;
    -o-transition: all 0.4s ease-in-out;
    -webkit-transition: all 0.4s ease-in-out;
    &:hover {
      background: linear-gradient(90deg, #00d696 0%, #00ba98 100%);
      color: var(--white);
    }
    &:active {
      background: linear-gradient(90deg, #19e985 0%, #00d696 100%);
      color: var(--white);
    }
  }
  &--center {
    justify-content: center;
  }
  &--left {
    justify-content: flex-start;
  }
  &--right {
    justify-content: flex-end;
  }
  &--green-light {
    color: white;
    background: linear-gradient(90deg, #01d596 0%, #01bd98 100%);
  }
}
</style>
